import React, { useEffect, useRef, useState } from 'react'
import './StepTracker.css'

const STEP_MS = 3400

/**
 * "How it works" as a live job tracker: a marker travels along the track and each step shows
 * Done / In progress / Up next, with its timing and what the customer gets. It plays once the
 * section is on screen, loops, and stops on the step a visitor clicks or hovers.
 * Reduced motion: shows the whole journey completed, without movement.
 */
function StepTracker({ steps, tracker = {} }) {
  const ref = useRef(null)
  const reduced = typeof window !== 'undefined' && window.matchMedia('(prefers-reduced-motion: reduce)').matches
  const [active, setActive] = useState(reduced ? steps.length - 1 : 0)
  const [playing, setPlaying] = useState(false)
  const [held, setHeld] = useState(false)

  // Start when the section scrolls into view
  useEffect(() => {
    if (reduced || !ref.current || typeof IntersectionObserver === 'undefined') return undefined
    const observer = new IntersectionObserver(([entry]) => setPlaying(entry.isIntersecting), { threshold: 0.35 })
    observer.observe(ref.current)
    return () => observer.disconnect()
  }, [reduced])

  useEffect(() => {
    if (!playing || held) return undefined
    // Pause a little longer on the last step so "completed" can be seen before starting again
    const delay = active === steps.length - 1 ? STEP_MS * 1.6 : STEP_MS
    const timer = setTimeout(() => setActive((current) => (current + 1) % steps.length), delay)
    return () => clearTimeout(timer)
  }, [playing, held, active, steps.length])

  const last = steps.length - 1
  const complete = active === last
  const progress = last > 0 ? active / last : 1
  const step = steps[active]

  return (
    <div className="stk" ref={ref} onMouseLeave={() => setHeld(false)}>
      <div className="stk__head">
        <span className="stk__job">
          <span className={`stk__live${complete ? ' is-done' : ''}`} aria-hidden="true" />
          {tracker.label || 'Your project'}
        </span>
        <span className="stk__status" aria-live="polite">
          {complete ? (
            <>
              <i className="fa-solid fa-circle-check" aria-hidden="true" /> All steps complete
            </>
          ) : (
            <>
              Step {active + 1} of {steps.length} · <strong>{step.title}</strong>
            </>
          )}
        </span>
      </div>

      {/* Track with the travelling marker */}
      <div className="stk__track" style={{ '--stk-progress': progress, '--stk-count': steps.length }} aria-hidden="true">
        <span className="stk__rail" />
        <span className="stk__fill" />
        <span className="stk__marker">
          <i className={`fa-solid ${tracker.icon || 'fa-location-arrow'}`} />
        </span>
      </div>

      <ol className="stk__steps">
        {steps.map((item, index) => {
          const state = index < active || complete ? 'done' : index === active ? 'current' : 'next'
          return (
            <li key={item.title}>
              <button
                type="button"
                className={`stk__step is-${state}`}
                aria-current={index === active ? 'step' : undefined}
                onClick={() => {
                  setActive(index)
                  setHeld(true)
                }}
                onMouseEnter={() => {
                  setActive(index)
                  setHeld(true)
                }}
              >
                <span className="stk__top">
                  <span className="stk__icon" aria-hidden="true">
                    <i className={`fa-solid ${state === 'done' ? 'fa-check' : item.icon}`} />
                  </span>
                  <span className={`stk__badge is-${state}`}>
                    {state === 'done' ? 'Done' : state === 'current' ? 'In progress' : 'Up next'}
                  </span>
                </span>
                <span className="stk__when">
                  <i className="fa-solid fa-clock" aria-hidden="true" />
                  {item.when}
                </span>
                <strong className="stk__title">
                  <span className="stk__num">{String(index + 1).padStart(2, '0')}</span>
                  {item.title}
                </strong>
                <span className="stk__text">{item.text}</span>
                {item.gets && (
                  <span className="stk__gets">
                    <i className="fa-solid fa-gift" aria-hidden="true" />
                    You get: <b>{item.gets}</b>
                  </span>
                )}
                {state === 'current' && !complete && playing && !held && (
                  <span key={active} className="stk__timer" style={{ animationDuration: `${STEP_MS}ms` }} aria-hidden="true" />
                )}
              </button>
            </li>
          )
        })}
      </ol>
    </div>
  )
}

export default StepTracker
